/**
 * NewKlienModal — form to create a klien_record via POST /api/klien.
 *
 * Schema (from packages/types/src/api.ts CreateKlienRequestSchema):
 *   full_name        string min 1 max 255   REQUIRED
 *   dob              string ISO date        REQUIRED
 *   parent_name      string                 optional
 *   parent_phone     string + phone regex   optional
 *   school_name      string                 optional
 *   is_left_handed   boolean                default false
 *   package_tier     "singkat"|"sedang"|"lengkap"  REQUIRED
 *   consent_given    literal true           REQUIRED
 *
 * Role: scan_station / agent / owner / super_admin (auth.franchisor_id required).
 * Response: 201 with full klien row (id, batch_id, etc.).
 */
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { apiPost } from "../api.js";
import { Button } from "./ui/index.js";

const TIERS = [
  { value: "singkat" as const, label: "Singkat", desc: "1 halaman · pemetaan dasar" },
  { value: "sedang" as const, label: "Sedang", desc: "6 halaman · Pro" },
  { value: "lengkap" as const, label: "Lengkap", desc: "10 halaman · Premium" },
];

interface CreatedKlien {
  id: string;
  batch_id: string;
  full_name: string;
}

interface NewKlienModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (klien: CreatedKlien) => void;
}

const PHONE_RE = /^\+?[0-9\s\-()]{6,20}$/;

export function NewKlienModal({ open, onClose, onCreated }: NewKlienModalProps) {
  const [fullName, setFullName] = useState("");
  const [dob, setDob] = useState("");
  const [parentName, setParentName] = useState("");
  const [parentPhone, setParentPhone] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [isLeftHanded, setIsLeftHanded] = useState(false);
  const [packageTier, setPackageTier] = useState<"singkat" | "sedang" | "lengkap">("lengkap");
  const [consentGiven, setConsentGiven] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  function resetForm() {
    setFullName("");
    setDob("");
    setParentName("");
    setParentPhone("");
    setSchoolName("");
    setIsLeftHanded(false);
    setPackageTier("lengkap");
    setConsentGiven(false);
    setFieldErrors({});
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!fullName.trim()) errs.full_name = "Nama wajib diisi";
    else if (fullName.length > 255) errs.full_name = "Maksimal 255 karakter";
    if (!dob) errs.dob = "Tanggal lahir wajib diisi";
    if (parentPhone && !PHONE_RE.test(parentPhone))
      errs.parent_phone = "Format nomor tidak valid (contoh: +62 812-3456-7890)";
    if (!consentGiven) errs.consent_given = "Persetujuan wajib dicentang";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  const createMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        full_name: fullName.trim(),
        dob,
        is_left_handed: isLeftHanded,
        package_tier: packageTier,
        consent_given: true,
      };
      if (parentName.trim()) body.parent_name = parentName.trim();
      if (parentPhone.trim()) body.parent_phone = parentPhone.trim();
      if (schoolName.trim()) body.school_name = schoolName.trim();
      return apiPost<CreatedKlien>("api/klien", body);
    },
    onSuccess: (klien) => {
      onCreated(klien);
      resetForm();
      onClose();
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    createMutation.mutate();
  }

  function handleClose() {
    if (createMutation.isPending) return;
    resetForm();
    onClose();
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 bg-brand-950/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.form
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSubmit}
            className="bg-white border border-surface-3 rounded-2xl shadow-depth-3 p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-brand-900">Klien Baru</h2>
              <p className="text-xs text-slate-500 mt-1">Isi data dasar klien sebelum scan sidik jari.</p>
            </div>

            <div className="space-y-3.5">
              {/* Full name */}
              <div>
                <label htmlFor="full_name" className="block text-xs font-medium text-slate-700 mb-1.5">
                  Nama lengkap <span className="text-tabrakan">*</span>
                </label>
                <input
                  id="full_name"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="input"
                  placeholder="Contoh: Andi Pratama"
                  autoFocus
                />
                {fieldErrors.full_name && (
                  <p className="text-[11px] text-tabrakan mt-1">{fieldErrors.full_name}</p>
                )}
              </div>

              {/* DOB */}
              <div>
                <label htmlFor="dob" className="block text-xs font-medium text-slate-700 mb-1.5">
                  Tanggal lahir <span className="text-tabrakan">*</span>
                </label>
                <input
                  id="dob"
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  className="input"
                  max={new Date().toISOString().slice(0, 10)}
                />
                {fieldErrors.dob && (
                  <p className="text-[11px] text-tabrakan mt-1">{fieldErrors.dob}</p>
                )}
              </div>

              {/* Parent name + phone */}
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="parent_name" className="block text-xs font-medium text-slate-700 mb-1.5">
                    Nama orang tua
                  </label>
                  <input
                    id="parent_name"
                    type="text"
                    value={parentName}
                    onChange={(e) => setParentName(e.target.value)}
                    className="input"
                    placeholder="Opsional"
                  />
                </div>
                <div>
                  <label htmlFor="parent_phone" className="block text-xs font-medium text-slate-700 mb-1.5">
                    No. HP orang tua
                  </label>
                  <input
                    id="parent_phone"
                    type="tel"
                    value={parentPhone}
                    onChange={(e) => setParentPhone(e.target.value)}
                    className="input"
                    placeholder="+62 812-3456-7890"
                  />
                  {fieldErrors.parent_phone && (
                    <p className="text-[11px] text-tabrakan mt-1">{fieldErrors.parent_phone}</p>
                  )}
                </div>
              </div>

              {/* School */}
              <div>
                <label htmlFor="school_name" className="block text-xs font-medium text-slate-700 mb-1.5">
                  Sekolah
                </label>
                <input
                  id="school_name"
                  type="text"
                  value={schoolName}
                  onChange={(e) => setSchoolName(e.target.value)}
                  className="input"
                  placeholder="Opsional"
                />
              </div>

              {/* Left-handed */}
              <label className="flex items-center gap-2 cursor-pointer group select-none">
                <input
                  type="checkbox"
                  checked={isLeftHanded}
                  onChange={(e) => setIsLeftHanded(e.target.checked)}
                  className="rounded border-slate-300 text-brand-500 focus:ring-brand-500 focus:ring-offset-1 w-4 h-4"
                />
                <span className="text-xs text-slate-700 group-hover:text-brand-700 transition-colors">
                  Klien kidal (mempengaruhi pembacaan delta L/R)
                </span>
              </label>

              {/* Package tier — visual radio cards */}
              <div>
                <div className="block text-xs font-medium text-slate-700 mb-1.5">
                  Paket laporan <span className="text-tabrakan">*</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {TIERS.map((tier) => {
                    const active = packageTier === tier.value;
                    return (
                      <label
                        key={tier.value}
                        className={`cursor-pointer rounded-lg border-2 p-2 text-center transition-all ${
                          active
                            ? "bg-brand-50 border-brand-400 shadow-depth-1"
                            : "bg-white border-surface-3 hover:border-brand-200 hover:bg-surface-1"
                        }`}
                      >
                        <input
                          type="radio"
                          name="package_tier"
                          value={tier.value}
                          checked={active}
                          onChange={() => setPackageTier(tier.value)}
                          className="sr-only"
                        />
                        <div className={`text-xs font-semibold ${active ? "text-brand-700" : "text-slate-700"}`}>
                          {tier.label}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{tier.desc}</div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Consent */}
              <div className={`rounded-lg border ${
                fieldErrors.consent_given
                  ? "border-tabrakan/40 bg-red-50/40"
                  : "border-surface-3 bg-surface-1"
              } px-3 py-2.5`}>
                <label className="flex items-start gap-2 cursor-pointer group select-none">
                  <input
                    type="checkbox"
                    checked={consentGiven}
                    onChange={(e) => setConsentGiven(e.target.checked)}
                    className="rounded border-slate-300 text-brand-500 focus:ring-brand-500 focus:ring-offset-1 w-4 h-4 mt-0.5"
                  />
                  <span className="text-[12px] text-slate-700 leading-snug">
                    <strong>Saya/orang tua menyetujui</strong> pengambilan sidik jari & pemrosesan oleh DCPPI sesuai kebijakan privasi.
                  </span>
                </label>
                {fieldErrors.consent_given && (
                  <p className="text-[11px] text-tabrakan mt-1.5 ml-6">{fieldErrors.consent_given}</p>
                )}
              </div>

              {/* Server error */}
              {createMutation.isError && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                >
                  {String(createMutation.error)}
                </motion.div>
              )}
            </div>

            <div className="flex gap-2 justify-end mt-5">
              <Button variant="ghost" type="button" onClick={handleClose} disabled={createMutation.isPending}>
                Batal
              </Button>
              <Button variant="primary" type="submit" loading={createMutation.isPending}>
                Buat Klien
              </Button>
            </div>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
